import React, { useEffect } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Box,
} from '@mui/material';
import { useStore, navStore } from '@mcn-platform/models';
import { WysiwygEditor, deserializeMarkdown } from '@mcn-platform/portal-lib';
import BulletPoints from './bulletItemsInput/BulletItems';
import AIQuizDisplay from './AIQuizDisplay';

interface GenerateAIDialogType {
  open: boolean;
  onClose: () => void;
  title: string;
  promptList: any[],
  itemType: string,
  itemKey: string,
  onConfirm: (newValue: any) => void;
  onCancel: () => void;
  aiContentType: string,
}

const GenerateAIDialog = ({
  open,
  onClose,
  title,
  promptList,
  itemType,
  itemKey,
  onConfirm,
  onCancel,
  aiContentType,
}: GenerateAIDialogType) => {
  const [prompt, setPrompt] = React.useState(promptList[0]);
  const [result, setResult] = React.useState<string | any>();
  const [wysiwygID, setWysiwygID] = React.useState<string>('generate_ai');
  const store = useStore();

  const updatedValue = (slateValue:any) => {
    console.log('slate value', slateValue);
    // console.log('rawText value', rawText);
    const newValue = {
      slate: slateValue,
      html: null,
    };
    console.log('New rich text value in AI Dialog', newValue);
    setResult(newValue.slate);
  };

  const handleConfirm = async () => {
    onConfirm(result);
  };

  const handleCancel = async () => {
    onCancel();
  };

  const handleGenerate = async () => {
    // console.log('Result: ', createdData);
    navStore.showMessageDialog(
      'Generating AI',
      'Please wait while generating content',
    );

    let finalPrompt = prompt;
    switch (aiContentType) {
      case ('quiz'):
        finalPrompt = {
          instruction: `Generate exactly ${prompt} multiple choice quiz questions as JSON. Each question must conform to the IQuestion type and have 3 or 4 choices.`,
          format: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                id: {
                  type: 'string',
                  description: 'A unique identifier for the question (UUID or unique string)',
                },
                questionText: {
                  type: 'string',
                  description: 'The text of the question',
                },
                answers: {
                  type: 'array',
                  description: 'An array of possible answers, each may include answerText and optional explanation',
                  items: {
                    type: 'object',
                    properties: {
                      answerText: {
                        type: 'string',
                        description: 'The text of the answer option',
                      },
                      explanation: {
                        type: 'string',
                        description: 'Optional explanation for the answer',
                        nullable: true,
                      },
                    },
                    required: ['answerText'],
                  },
                },
                options: {
                  type: 'array',
                  description: 'A simplified array of answerText strings from the answers field',
                  items: { type: 'string' },
                },
                type: {
                  type: 'string',
                  enum: ['multipleChoiceQuestion'],
                  description: 'The type of question',
                },
                correctAnswer: {
                  type: 'array',
                  description: 'The answerText values that are correct',
                  items: { type: 'string' },
                },
              },
              required: ['id', 'questionText', 'answers', 'options', 'type', 'correctAnswer'],
            },
          },
        };
        break;
      case ('bulletPoints'):
        finalPrompt = {
          instruction: `Generate exactly ${prompt} bullet points as JSON.`,
          format: {
            type: 'array',
            items: {
              type: 'string',
              description: 'A single learning objective',
            },
          },
          additionalProperties: false,
        };
        break;
      default:
        break;
    }

    const generatedAIResult = await store.generateField(
      itemType,
      itemKey,
      finalPrompt,
    );
    if (generatedAIResult) {
      // const value = processor.parse(generatedAIResult);
      // console.log('marked text', value);
      // updatedValue([value]);
      if (aiContentType === 'standardText') {
        const markdownToPlate = deserializeMarkdown(generatedAIResult);
        console.log('markdownToPlate', markdownToPlate);
        setResult(markdownToPlate);
        setWysiwygID(`generate_ai.${Date.now()}`);
      } else {
        setResult(generatedAIResult);
      }
      navStore.hideMessageDialog();
    } else {
      navStore.hideMessageDialog();
      navStore.showConfirmDialog(
        'Generating AI',
        'Error generating content',
        () => navStore.hideMessageDialog(),
        'Ok',
      );
    }
  };
  useEffect(() => {
    if (open && aiContentType === 'bulletPoints') {
      setResult('');
    }
  }, [open, aiContentType]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2}>
            {(() => {
            switch (aiContentType) {
              case 'bulletPoints':
                return (
                  <>
                    <Box sx={{ whiteSpace: 'nowrap' }}>
                      How many responses would you like to generate?
                    </Box>
                    <TextField
                      select
                      size="small"
                      value={prompt || 3}
                      onChange={(event) => setPrompt(Number(event.target.value))}
                      SelectProps={{
                        native: true,
                      }}
                      sx={{ width: 80 }}
                    >
                      {[...Array(9)].map((_, i) => {
                        const value = i + 1;
                        return (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        );
                      })}
                    </TextField>
                  </>
                );

              case 'quiz':
                return (
                  <>
                    <Box sx={{ whiteSpace: 'nowrap' }}>
                      How many questions would you like to generate?
                    </Box>
                    <TextField
                      select
                      size="small"
                      value={prompt || 3}
                      onChange={(event) => setPrompt(Number(event.target.value))}
                      SelectProps={{
                        native: true,
                      }}
                      sx={{ width: 80 }}
                    >
                      {[...Array(25)].map((_, i) => {
                        const value = i + 1;
                        return (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        );
                      })}
                    </TextField>
                  </>
                );

              default:
                return (
                  <Autocomplete
                    freeSolo
                    fullWidth
                    id="promptAutocomplete"
                    options={promptList}
                    onChange={(event: any) => {
                      console.log('event', event);
                      setPrompt(promptList[event.target.value]);
                    }}
                    value={promptList[0]}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        placeholder="Enter or modify Prompt"
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                      />
                    )}
                  />
                );
            }
          })()}
            <Button
              variant="outlined"
              onClick={handleGenerate}
            >
              Generate
            </Button>
          </Stack>
          {(() => {
            switch (aiContentType) {
              case 'richText':
                return (
                  <Box
                    sx={[{
                      width: '100%',
                      border: '1px solid',
                      borderColor: '#e0e0eb',
                      borderRadius: 1,
                      padding: 2,
                    },
                    {
                      '&:hover': {
                        borderColor: '#111111',
                      },
                    },
                    {
                      '&:focus': {
                        border: '2px solid #1276dd',
                      },
                    },
                    ]}
                  >
                    <WysiwygEditor
                      key={wysiwygID}
                      id={wysiwygID}
                      value={result}
                      setValue={(newValue: any) => { updatedValue(newValue); }}
                    />
                  </Box>
                );

              case 'bulletPoints': {
                return (
                  <BulletPoints
                    value={result}
                    setValue={(val: string) => setResult(val)}
                    placeholder="Add Learning Objectives..."
                    disabled={false}
                  />
                );
              }
              case 'quiz': {
                return (
                  <AIQuizDisplay
                    value={result}
                    placeholder="Add Quiz Questions..."
                  />
                );
              }

              default:
                return (
                  <TextField
                    value={result}
                    placeholder="Generated content"
                    fullWidth
                    multiline
                    rows={16}
                    onChange={(event) => setResult(event.target.value)}
                  />
                );
            }
          })()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!result} color="primary" variant="outlined">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateAIDialog;
